import { useQuery, useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { fetchNamespaces, stopNamespace, startNamespace } from '@/api/namespaces';

const NAMESPACES_KEY: QueryKey = ['namespaces'];

// Burst-poll the namespace list for ~60s after a Start/Stop action so the
// UI catches state transitions (running → stopped, etc.) quickly without
// paying the bandwidth cost of always-fast polling. Mirrors the helper in
// useAwsResources for consistency.
const BURST_INTERVAL_MS = 3_000;
const BURST_DURATION_MS = 60_000;

function startBurstPoll(queryClient: ReturnType<typeof useQueryClient>, queryKey: QueryKey) {
  const deadline = Date.now() + BURST_DURATION_MS;
  const tick = () => {
    if (Date.now() > deadline) return;
    queryClient.invalidateQueries({ queryKey });
    window.setTimeout(tick, BURST_INTERVAL_MS);
  };
  window.setTimeout(tick, BURST_INTERVAL_MS);
}

export function useNamespaces(includeProtected = false) {
  return useQuery({
    queryKey: [...NAMESPACES_KEY, { includeProtected }],
    queryFn: () => fetchNamespaces(includeProtected),
    staleTime: 15 * 1000,
    refetchInterval: (query) => {
      // Don't poll when the feature isn't configured (503) or auth is denied.
      // Hammering the backend on a known config error wastes server cycles
      // and clutters the network panel for whoever is debugging.
      const err = query.state.error;
      if (err instanceof AxiosError) {
        const status = err.response?.status;
        if (status === 503 || status === 403 || status === 401) return false;
      }
      return 30 * 1000;
    },
    retry: (failureCount, err) => {
      // Same logic for retries on initial load.
      if (err instanceof AxiosError) {
        const status = err.response?.status;
        if (status === 503 || status === 403 || status === 401) return false;
      }
      return failureCount < 2;
    },
  });
}

export function useStopNamespace() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (namespace: string) => stopNamespace(namespace),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NAMESPACES_KEY });
      startBurstPoll(queryClient, NAMESPACES_KEY);
    },
  });
}

export function useStartNamespace() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (namespace: string) => startNamespace(namespace),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NAMESPACES_KEY });
      startBurstPoll(queryClient, NAMESPACES_KEY);
    },
  });
}
